import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { JSX, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldProps, useValidatedForm } from '../../library/formValidation';
import { IAppointment } from '../../library/models/appointment';
import { useApiFormError } from '../../library/useApiFormError';
import { useIsCompact } from '../../library/useIsCompact';
import ResourcesIds from '../../resources/ResourcesIds';
import { useReactMutation } from '../../services/queryService';
import { services } from '../../services/services';
import {
    blockedHintStyles,
    descriptionStyles,
    dialogFormStyles,
    formAlertStyles,
    reasonFieldStyles,
} from './declineRequestDialog.styles';
import {
    createDeclineRequestSchema,
    DECLINE_REASON_MAX,
    DeclineRequestFormValues,
} from './declineRequestSchema';

const DEFAULT_VALUES: DeclineRequestFormValues = { reason: '' };

export interface IDeclineRequestDialogProps {
    /** The request being turned down, or null when the dialog is closed. */
    appointment: IAppointment | null;
    onClose: () => void;
    /** It was declined: the host closes whatever it opened this from and refreshes. */
    onDeclined: () => void;
}

/**
 * Turning a patient's booking request down, with an optional word for them.
 *
 * A dialog of its own rather than a button that fires at once, for the one thing it adds:
 * the note. A decline is final and emailed, so what the therapist would have said on the
 * phone -- "I am away that morning, the afternoon is free" -- is worth one more step, and it
 * is the only way to offer another hour, since a pending request is never moved by staff.
 * The field is optional because a decline with nothing to say is still a decline.
 */
export const DeclineRequestDialog = (props: IDeclineRequestDialogProps): JSX.Element => {
    const { appointment, onClose, onDeclined } = props;
    const { t: translate } = useTranslation();
    const isCompact = useIsCompact();

    const schema = useMemo(() => createDeclineRequestSchema(translate), [translate]);

    const {
        register, handleSubmit, reset, setError, formState: { errors, isValid },
    } = useValidatedForm<DeclineRequestFormValues>(schema, { defaultValues: DEFAULT_VALUES });

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<DeclineRequestFormValues>(
        setError, knownFields, translate, ResourcesIds.APPOINTMENT_REQUEST_ACTION_FAILED);

    const decline = useReactMutation(services.appointments.declineRequestMutation());

    // A note written for one patient must never be sent to the next.
    useEffect(() => {
        if (appointment) {
            reset(DEFAULT_VALUES);
            clear();
        }
    }, [appointment, reset, clear]);

    const onValid = (values: DeclineRequestFormValues): void => {
        if (!appointment) {
            return;
        }

        clear();

        decline.mutate(
            { appointmentId: appointment.id, reason: values.reason.trim() || undefined },
            { onSuccess: onDeclined, onError: report }
        );
    };

    return (
        <Dialog open={!!appointment} onClose={onClose} fullWidth maxWidth="xs" fullScreen={isCompact}>
            <DialogTitle>{translate(ResourcesIds.DECLINE_REQUEST_TITLE)}</DialogTitle>

            <Box component="form" onSubmit={handleSubmit(onValid)} noValidate sx={dialogFormStyles}>
                <DialogContent>
                    {formError && <Alert severity="error" sx={formAlertStyles}>{formError}</Alert>}

                    <Typography variant="body2" sx={descriptionStyles}>
                        {translate(ResourcesIds.DECLINE_REQUEST_DESCRIPTION, {
                            name: appointment?.patientName ?? '',
                        })}
                    </Typography>

                    <TextField
                        {...register('reason')}
                        fullWidth
                        multiline
                        minRows={3}
                        autoFocus
                        sx={reasonFieldStyles}
                        label={translate(ResourcesIds.DECLINE_REQUEST_REASON_LABEL)}
                        slotProps={{ htmlInput: { maxLength: DECLINE_REASON_MAX } }}
                        {...fieldProps(errors, 'reason',
                            translate(ResourcesIds.DECLINE_REQUEST_REASON_HINT))}
                    />
                </DialogContent>

                <DialogActions>
                    {!isValid && (
                        <Typography variant="caption" color="text.secondary" sx={blockedHintStyles}>
                            {translate(ResourcesIds.FORM_BLOCKED_SAVE)}
                        </Typography>
                    )}

                    <Button onClick={onClose} disabled={decline.isPending}>
                        {translate(ResourcesIds.CLOSE_DIALOG)}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="error"
                        disabled={!isValid || decline.isPending}
                    >
                        {translate(ResourcesIds.DECLINE_REQUEST_SUBMIT)}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
